/** Stable key to group products that represent the same listing (one product page, multiple sellers). */
export function getListingGroupKey(product: any): string {
  // Group by product page identity (itemNumber/ASIN or URL), NOT by listing_id —
  // the backend assigns a unique listing_id per seller row, which would split one listing into multiple cards.
  const itemNumber = product?.itemNumber ?? product?.item_number;
  if (itemNumber && String(itemNumber).trim()) {
    return `item:${String(itemNumber).trim()}`;
  }
  const url = product?.url || product?.current_url || '';
  if (!url || typeof url !== 'string') return `raw:${JSON.stringify(product?.title ?? '')}`;
  try {
    const u = new URL(url);
    const path = (u.pathname || '/').replace(/\/+$/, '') || '/';
    return `url:${u.hostname.toLowerCase()}${path}`;
  } catch {
    return `url:${String(url).slice(0, 200)}`;
  }
}

export type ReviewListItemLike = {
  id: string;
  url: string;
  crawled_at: string;
  brand_name?: string;
  product_name?: string;
  products: any[];
};

export type GroupedProductEntry = {
  id: string;
  groupKey: string;
  parentId: string;
  index: number;
  product: any;
  crawledAt: string;
  url: string;
  taskBrand?: string;
  taskProduct?: string;
  /** All products in this group (same listing, different seller rows); used for inject + mark-reviewed */
  allProductsInGroup: Array<{ parentId: string; index: number; product: any }>;
};

export function groupReviewItems(items: ReviewListItemLike[]): GroupedProductEntry[] {
  const flat: Array<{
    parentId: string;
    index: number;
    product: any;
    crawledAt: string;
    url: string;
    taskBrand?: string;
    taskProduct?: string;
  }> = [];
  items.forEach(item => {
    item.products
      .filter((p: any) => !(p && p.is_duplicate))
      .forEach((product: any, index: number) => {
        flat.push({
          parentId: item.id,
          index,
          product,
          crawledAt: item.crawled_at,
          url: item.url,
          taskBrand: item.brand_name,
          taskProduct: item.product_name,
        });
      });
  });

  const byKey = new Map<string, typeof flat>();
  flat.forEach(entry => {
    const key = getListingGroupKey(entry.product);
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(entry);
  });

  const groups: GroupedProductEntry[] = [];
  byKey.forEach((entries, groupKey) => {
    const first = entries[0];
    groups.push({
      id: groupKey,
      groupKey,
      parentId: first.parentId,
      index: first.index,
      product: first.product,
      crawledAt: first.crawledAt,
      url: first.url,
      taskBrand: first.taskBrand,
      taskProduct: first.taskProduct,
      allProductsInGroup: entries.map(e => ({
        parentId: e.parentId,
        index: e.index,
        product: e.product,
      })),
    });
  });
  return groups;
}
